// Détail d'une carte Kanban : assignés, checklist, labels, commentaires
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, X, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";
import { PRIORITES, COULEURS_LABELS } from "@/lib/constants";
import { formatDateHeure } from "@/lib/dates";

export const CardDialog = ({ open, onClose, carte, users, canEdit, onSaved }) => {
  const [form, setForm] = useState(null);
  const [commentaire, setCommentaire] = useState("");
  const [nouvelItem, setNouvelItem] = useState("");
  const [nouveauLabel, setNouveauLabel] = useState({ nom: "", couleur: COULEURS_LABELS[0] });

  useEffect(() => {
    if (open && carte) setForm({ ...carte, checklist: [...(carte.checklist || [])], labels: [...(carte.labels || [])], commentaires: [...(carte.commentaires || [])] });
  }, [open, carte]);

  if (!form) return null;

  const set = (champ, valeur) => setForm((f) => ({ ...f, [champ]: valeur }));

  const sauver = async () => {
    try {
      await api.patch(`/cards/${carte.id}`, {
        titre: form.titre, description: form.description, priorite: form.priorite,
        echeance: form.echeance || null, assignes: form.assignes,
        checklist: form.checklist, labels: form.labels,
      });
      toast.success("Carte enregistrée");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const supprimer = async () => {
    if (!window.confirm("Supprimer cette carte ?")) return;
    try {
      await api.delete(`/cards/${carte.id}`);
      toast.success("Carte supprimée");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const commenter = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/cards/${carte.id}/comments`, { texte: commentaire });
      set("commentaires", [...form.commentaires, data]);
      setCommentaire("");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const ajouterItem = (e) => {
    e.preventDefault();
    if (!nouvelItem.trim()) return;
    set("checklist", [...form.checklist, { id: crypto.randomUUID(), texte: nouvelItem.trim(), fait: false }]);
    setNouvelItem("");
  };

  const ajouterLabel = () => {
    if (!nouveauLabel.nom.trim()) return;
    set("labels", [...form.labels, { ...nouveauLabel, nom: nouveauLabel.nom.trim() }]);
    setNouveauLabel({ nom: "", couleur: COULEURS_LABELS[0] });
  };

  const basculerAssigne = (id) =>
    set("assignes", form.assignes.includes(id) ? form.assignes.filter((x) => x !== id) : [...form.assignes, id]);

  const fait = form.checklist.filter((c) => c.fait).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Détail de la carte</DialogTitle>
          <DialogDescription>Assignés, échéance, priorité, checklist, labels et commentaires.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <Label>Titre</Label>
            <Input value={form.titre} onChange={(e) => set("titre", e.target.value)} disabled={!canEdit} className="rounded-xl mt-1.5" data-testid="card-titre-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} disabled={!canEdit} className="rounded-xl mt-1.5" data-testid="card-description-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Priorité</Label>
              <Select value={form.priorite} onValueChange={(v) => set("priorite", v)} disabled={!canEdit}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="card-priorite-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Échéance</Label>
              <Input type="date" value={form.echeance || ""} onChange={(e) => set("echeance", e.target.value)} disabled={!canEdit} className="rounded-xl mt-1.5" data-testid="card-echeance-input" />
            </div>
            <div>
              <Label>Assigné(s)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" disabled={!canEdit} className="w-full rounded-xl mt-1.5 justify-start gap-2" data-testid="card-assignes-trigger">
                    <Users className="h-4 w-4" />
                    {form.assignes.length ? `${form.assignes.length} membre${form.assignes.length > 1 ? "s" : ""}` : "Choisir"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted cursor-pointer transition-colors">
                      <Checkbox checked={form.assignes.includes(u.id)} onCheckedChange={() => basculerAssigne(u.id)} data-testid={`card-assigne-${u.id}`} />
                      <UserAvatar user={u} size="sm" />
                      <span className="text-sm">{u.name}</span>
                    </label>
                  ))}
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* ——— Labels ——— */}
          <div>
            <Label>Labels</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {form.labels.map((l, i) => (
                <span key={i} className="flex items-center gap-1 text-xs font-medium text-white rounded-full pl-2.5 pr-1 py-1" style={{ background: l.couleur }}>
                  {l.nom}
                  {canEdit && (
                    <button onClick={() => set("labels", form.labels.filter((_, j) => j !== i))} className="p-0.5 rounded-full hover:bg-white/20 transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
              {canEdit && (
                <span className="flex items-center gap-1.5">
                  <Input value={nouveauLabel.nom} onChange={(e) => setNouveauLabel({ ...nouveauLabel, nom: e.target.value })} placeholder="Nouveau label" className="h-8 w-32 rounded-full text-xs" data-testid="card-label-input" />
                  {COULEURS_LABELS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNouveauLabel({ ...nouveauLabel, couleur: c })}
                      className={`h-5 w-5 rounded-full transition-transform ${nouveauLabel.couleur === c ? "scale-125 ring-2 ring-ring ring-offset-1" : ""}`}
                      style={{ background: c }}
                    />
                  ))}
                  <button onClick={ajouterLabel} className="p-1.5 rounded-full bg-muted hover:bg-muted/70 transition-colors" data-testid="card-label-add">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
            </div>
          </div>

          {/* ——— Checklist ——— */}
          <div>
            <Label>Checklist {form.checklist.length > 0 && `(${fait}/${form.checklist.length})`}</Label>
            <div className="space-y-1.5 mt-1.5">
              {form.checklist.map((item, i) => (
                <div key={item.id || i} className="flex items-center gap-2.5 rounded-lg px-2 py-1">
                  <Checkbox
                    checked={item.fait}
                    disabled={!canEdit}
                    onCheckedChange={(v) => set("checklist", form.checklist.map((c, j) => (j === i ? { ...c, fait: Boolean(v) } : c)))}
                    data-testid={`card-checklist-item-${i}`}
                  />
                  <span className={`flex-1 text-sm ${item.fait ? "line-through text-muted-foreground" : ""}`}>{item.texte}</span>
                  {canEdit && (
                    <button onClick={() => set("checklist", form.checklist.filter((_, j) => j !== i))} className="p-1 text-muted-foreground hover:text-destructive transition-colors">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {canEdit && (
                <form onSubmit={ajouterItem} className="flex gap-2">
                  <Input value={nouvelItem} onChange={(e) => setNouvelItem(e.target.value)} placeholder="Ajouter un élément…" className="rounded-xl h-9" data-testid="card-checklist-input" />
                  <Button type="submit" size="sm" variant="secondary" className="rounded-full" data-testid="card-checklist-add">Ajouter</Button>
                </form>
              )}
            </div>
          </div>

          {/* ——— Commentaires ——— */}
          <div>
            <Label>Commentaires</Label>
            <div className="space-y-2.5 mt-1.5">
              {form.commentaires.map((c) => (
                <div key={c.id} className="rounded-xl bg-muted/50 px-4 py-2.5">
                  <p className="text-xs font-semibold">{c.auteur_nom} <span className="font-normal text-muted-foreground">· {formatDateHeure(c.created_at)}</span></p>
                  <p className="text-sm mt-0.5">{c.texte}</p>
                </div>
              ))}
              <form onSubmit={commenter} className="flex gap-2">
                <Input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="Écrire un commentaire…" className="rounded-xl h-9" data-testid="card-comment-input" />
                <Button type="submit" size="sm" disabled={!commentaire.trim()} className="rounded-full" data-testid="card-comment-submit">Envoyer</Button>
              </form>
            </div>
          </div>
        </div>
        {canEdit && (
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={supprimer} className="text-destructive gap-1.5 rounded-full mr-auto" data-testid="card-delete-button">
              <Trash2 className="h-4 w-4" /> Supprimer
            </Button>
            <Button onClick={sauver} className="rounded-full" data-testid="card-save-button">Enregistrer</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
